import { isSupabaseConfigured, supabase } from './supabaseClient.js';

const counterScope = 'campusguide_global';
const visitSessionKey = 'campusguide-visitor-counted-session';
const DEFAULT_FALLBACK_BASE = 2847;

const withTimeout = (promise, ms = 2000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Counter request timed out')), ms)),
  ]);
};

export const getFallbackCount = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_FALLBACK_BASE;
  }

  try {
    const stored = window.localStorage.getItem('campusguide-fallback-visit-count');
    const parsed = Number(stored);
    if (Number.isFinite(parsed) && parsed >= DEFAULT_FALLBACK_BASE) {
      return parsed;
    }
    window.localStorage.setItem('campusguide-fallback-visit-count', String(DEFAULT_FALLBACK_BASE));
    return DEFAULT_FALLBACK_BASE;
  } catch (_e) {
    return DEFAULT_FALLBACK_BASE;
  }
};

const setFallbackCount = (count) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem('campusguide-fallback-visit-count', String(count));
  } catch (_e) {
    // Ignore storage errors
  }
};

export const getSharedVisitCount = async () => {
  if (!isSupabaseConfigured || !supabase) {
    return getFallbackCount();
  }

  try {
    const query = supabase
      .from('site_visit_metrics')
      .select('total_visits')
      .eq('scope', counterScope)
      .maybeSingle();

    const { data, error } = await withTimeout(query, 2000);

    if (error || !data) {
      return getFallbackCount();
    }

    const numeric = Number(data.total_visits);
    if (Number.isFinite(numeric) && numeric > 0) {
      setFallbackCount(numeric);
      return numeric;
    }

    return getFallbackCount();
  } catch (_e) {
    return getFallbackCount();
  }
};

export const syncSharedVisitCount = async () => {
  const alreadyCountedThisSession =
    typeof window !== 'undefined' && window.sessionStorage.getItem(visitSessionKey) === 'true';

  const handleFallback = () => {
    const current = getFallbackCount();
    if (!alreadyCountedThisSession) {
      const next = current + 1;
      setFallbackCount(next);
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(visitSessionKey, 'true');
        } catch (_e) {}
      }
      return next;
    }
    return current;
  };

  if (!isSupabaseConfigured || !supabase) {
    return handleFallback();
  }

  if (alreadyCountedThisSession) {
    return getSharedVisitCount();
  }

  try {
    const rpcPromise = supabase.rpc('increment_site_visit', {
      visit_scope: counterScope,
    });

    const { data, error } = await withTimeout(rpcPromise, 2000);

    if (error || data == null) {
      return handleFallback();
    }

    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(visitSessionKey, 'true');
      } catch (_e) {}
    }

    const numeric = Number(data);
    if (Number.isFinite(numeric) && numeric > 0) {
      setFallbackCount(numeric);
      return numeric;
    }

    return handleFallback();
  } catch (_err) {
    return handleFallback();
  }
};
